import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Megaphone, Plus, Send, Building2, BookOpen, Users,
  Layers, AlertTriangle, Info, Calendar, Clock, Trash2,
  Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  announcement: { icon: Megaphone, color: 'bg-blue-100 text-blue-700', label: 'Annonce' },
  info: { icon: Info, color: 'bg-green-100 text-green-700', label: 'Information' },
  urgent: { icon: AlertTriangle, color: 'bg-red-100 text-red-700', label: 'Urgent' },
  schedule_change: { icon: Calendar, color: 'bg-orange-100 text-orange-700', label: 'Changement horaire' },
};

const SCOPE_CONFIG = {
  institution: { icon: Building2, color: 'bg-purple-100 text-purple-700' },
  faculty: { icon: Building2, color: 'bg-indigo-100 text-indigo-700' },
  department: { icon: Layers, color: 'bg-teal-100 text-teal-700' },
  class: { icon: Users, color: 'bg-yellow-100 text-yellow-700' },
  course: { icon: BookOpen, color: 'bg-pink-100 text-pink-700' },
};

const AnnouncementCard = ({ ann, isOwn, onDelete }) => {
  const typeConf = TYPE_CONFIG[ann.type] || TYPE_CONFIG.announcement;
  const scopeConf = SCOPE_CONFIG[ann.scope_type] || SCOPE_CONFIG.institution;
  const TypeIcon = typeConf.icon;
  const ScopeIcon = scopeConf.icon;
  const date = new Date(ann.published_at);
  const timeAgo = getTimeAgo(date);

  return (
    <Card className={`transition-all hover:shadow-sm ${ann.type === 'urgent' ? 'border-l-4 border-l-red-500' : ''}`} data-testid={`announcement-${ann.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge className={`${typeConf.color} text-[10px]`}>
                <TypeIcon className="w-3 h-3 mr-0.5" />{typeConf.label}
              </Badge>
              <Badge variant="outline" className={`${scopeConf.color} text-[10px]`}>
                <ScopeIcon className="w-3 h-3 mr-0.5" />{ann.scope_label}: {ann.scope_name}
              </Badge>
            </div>
            <h3 className="font-semibold text-sm">{ann.title}</h3>
            <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{ann.content}</p>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
              <span>{ann.sender_name}</span>
              <span><Clock className="w-3 h-3 inline mr-0.5" />{timeAgo}</span>
            </div>
          </div>
          {isOwn && (
            <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => onDelete(ann.id)} data-testid={`delete-ann-${ann.id}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

function getTimeAgo(date) {
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "a l'instant";
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export const AnnouncementsPage = () => {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [total, setTotal] = useState(0);
  const [myAnns, setMyAnns] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDlg, setCreateDlg] = useState(false);
  const [filterScope, setFilterScope] = useState('all');
  const [creating, setCreating] = useState(false);

  // Form state
  const [formScope, setFormScope] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formType, setFormType] = useState('announcement');

  const loadFeed = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/announcements/feed`);
      setFeed(data.announcements || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadMyAnnouncements = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/announcements/mine`);
      setMyAnns(data);
    } catch (e) { console.error(e); }
  }, []);

  const loadScopes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/announcements/publishable-scopes`);
      setScopes(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    loadFeed();
    loadMyAnnouncements();
    loadScopes();
  }, [loadFeed, loadMyAnnouncements, loadScopes]);

  const handleCreate = async () => {
    if (!formScope || !formTitle.trim() || !formContent.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    const scope = scopes.find(s => `${s.scope_type}:${s.scope_unit_id}` === formScope);
    if (!scope) return;

    setCreating(true);
    try {
      await axios.post(`${API}/announcements`, {
        scope_type: scope.scope_type,
        scope_unit_id: scope.scope_unit_id,
        title: formTitle.trim(),
        content: formContent.trim(),
        type: formType,
      });
      toast.success('Annonce publiee !');
      setCreateDlg(false);
      setFormScope('');
      setFormTitle('');
      setFormContent('');
      setFormType('announcement');
      loadFeed();
      loadMyAnnouncements();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la publication');
    }
    finally { setCreating(false); }
  };

  const handleDelete = async (annId) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    try {
      await axios.delete(`${API}/announcements/${annId}`);
      toast.success('Annonce supprimee');
      loadFeed();
      loadMyAnnouncements();
    } catch (e) { toast.error('Erreur'); }
  };

  const filteredFeed = filterScope === 'all' ? feed : feed.filter(a => a.scope_type === filterScope);
  const canPublish = scopes.length > 0;
  const scopeTypes = [...new Set(feed.map(a => a.scope_type))];

  if (loading) return <div className="flex justify-center p-12"><div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="announcements-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2"><Megaphone className="w-5 h-5" /> Annonces</h1>
          <p className="text-sm text-gray-500">{total} annonce(s) visible(s)</p>
        </div>
        {canPublish && (
          <Button onClick={() => setCreateDlg(true)} data-testid="new-announcement-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle Annonce
          </Button>
        )}
      </div>

      <Tabs defaultValue="feed">
        <TabsList>
          <TabsTrigger value="feed" data-testid="tab-feed"><Megaphone className="w-4 h-4 mr-1" />Fil ({total})</TabsTrigger>
          {canPublish && <TabsTrigger value="mine" data-testid="tab-mine"><Send className="w-4 h-4 mr-1" />Mes annonces ({myAnns.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="feed">
          {/* Filter */}
          {scopeTypes.length > 1 && (
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-gray-400" />
              <div className="flex gap-1 flex-wrap">
                <Badge
                  className={`cursor-pointer text-xs ${filterScope === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  onClick={() => setFilterScope('all')}
                >Tout</Badge>
                {scopeTypes.map(st => (
                  <Badge
                    key={st}
                    className={`cursor-pointer text-xs ${filterScope === st ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    onClick={() => setFilterScope(st)}
                  >{SCOPE_CONFIG[st]?.color ? st.charAt(0).toUpperCase() + st.slice(1) : st}</Badge>
                ))}
              </div>
            </div>
          )}

          <ScrollArea className="h-[500px]">
            {filteredFeed.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-gray-400">
                <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>Aucune annonce pour le moment</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-2">
                {filteredFeed.map(a => (
                  <AnnouncementCard key={a.id} ann={a} isOwn={a.sender_user_id === user?.id} onDelete={handleDelete} />
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {canPublish && (
          <TabsContent value="mine">
            <ScrollArea className="h-[500px]">
              {myAnns.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-gray-400">
                  <Send className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Vous n'avez publie aucune annonce</p>
                </CardContent></Card>
              ) : (
                <div className="space-y-2">
                  {myAnns.map(a => (
                    <AnnouncementCard key={a.id} ann={a} isOwn={true} onDelete={handleDelete} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        )}
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={createDlg} onOpenChange={setCreateDlg}>
        <DialogContent className="max-w-lg" data-testid="create-announcement-dialog">
          <DialogHeader><DialogTitle>Nouvelle Annonce</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Perimetre de diffusion *</Label>
              <Select value={formScope} onValueChange={setFormScope}>
                <SelectTrigger data-testid="scope-select"><SelectValue placeholder="Choisir le perimetre" /></SelectTrigger>
                <SelectContent>
                  {scopes.map(s => (
                    <SelectItem key={`${s.scope_type}:${s.scope_unit_id}`} value={`${s.scope_type}:${s.scope_unit_id}`}>
                      [{s.label}] {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={formType} onValueChange={setFormType}>
                <SelectTrigger data-testid="type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="announcement">Annonce</SelectItem>
                  <SelectItem value="info">Information</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="schedule_change">Changement d'horaire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Titre *</Label>
              <Input value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="Titre de l'annonce" data-testid="ann-title-input" />
            </div>
            <div>
              <Label>Contenu *</Label>
              <Textarea value={formContent} onChange={e => setFormContent(e.target.value)} placeholder="Redigez votre annonce..." rows={4} data-testid="ann-content-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDlg(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={creating || !formScope || !formTitle.trim() || !formContent.trim()} data-testid="publish-btn">
              {creating ? 'Publication...' : 'Publier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
